import { MATCH_REASON_LABELS, SOURCE_LABELS, SUBMISSION_STATUS_LABELS, formatDateTime, formatPhone, type LeadSource, type SubmissionStatus } from '@takumi/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, GitMerge, Inbox, Plus, X } from 'lucide-react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { PageHeader } from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { Badge, Card, CardHeader, EmptyState, PageLoader } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { safeDecodeUri } from '@/lib/utils';
import { useInvalidateCrm } from '@/lib/queries';
import type { DuplicateCandidate } from '@/lib/types';

interface Review {
  id: string;
  candidates: DuplicateCandidate[];
  createdAt: string;
  submissionId: string;
  source: LeadSource;
  contactName: string | null;
  businessName: string | null;
  phoneE164: string | null;
  email: string | null;
  businessType: string | null;
  city: string | null;
  message: string | null;
  formName: string | null;
  receivedAt: string;
}

interface SubmissionRow {
  id: string;
  source: LeadSource;
  receivedAt: string;
  contactName: string | null;
  businessName: string | null;
  phoneE164: string | null;
  email: string | null;
  formName: string | null;
  pageUrl: string | null;
  utmSource: string | null;
  utmCampaign: string | null;
  gclid: string | null;
  fbclid: string | null;
  status: SubmissionStatus;
  organizationId: string | null;
  organizationName: string | null;
  error: string | null;
}

const STATUS_VARIANT: Record<SubmissionStatus, 'matcha' | 'gold' | 'today' | 'overdue' | 'neutral'> = {
  created: 'matcha',
  attached: 'gold',
  pending_review: 'today',
  rejected: 'neutral',
  error: 'overdue',
  received: 'neutral',
};

export function InboxPage() {
  const reviews = useQuery({ queryKey: ['reviews'], queryFn: () => api.get<Review[]>('/inbound/reviews'), refetchInterval: 60_000 });
  const subs = useQuery({ queryKey: ['submissions'], queryFn: () => api.get<SubmissionRow[]>('/inbound'), refetchInterval: 60_000 });
  if (!reviews.data || !subs.data) return <PageLoader />;
  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader title="פניות נכנסות" subtitle="כל פנייה מהאתר נשמרת כאן כפי שהתקבלה, יחד עם המקור והשיוך שלה" />
      {reviews.data.length > 0 && (
        <section className="mb-6 flex flex-col gap-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold">
            <AlertTriangle className="size-5 text-today" /> חשד לכפילות — נדרשת החלטה ({reviews.data.length})
          </h2>
          {reviews.data.map((r) => <ReviewCard key={r.id} review={r} />)}
        </section>
      )}
      <Card className="overflow-hidden">
        <CardHeader title="כל הפניות" icon={<Inbox />} count={subs.data.length} />
        {subs.data.length === 0 ? (
          <EmptyState icon={<Inbox />} title="עדיין לא התקבלו פניות" description="פניות מטפסי האתר יופיעו כאן אחרי התקנת התוסף takumi-crm-leads (ראו הגדרות › חיבורים)" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-sm">
              <thead className="bg-sunken/80 text-xs font-bold text-muted">
                <tr className="[&>th]:px-3 [&>th]:py-2.5 [&>th]:text-start">
                  <th className="ps-4">התקבל</th>
                  <th>פונה</th>
                  <th>טופס / עמוד</th>
                  <th>שיוך שיווקי</th>
                  <th>תוצאה</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line/70">
                {subs.data.map((s) => (
                  <tr key={s.id} className="align-top">
                    <td className="py-2.5 ps-4 pe-3 whitespace-nowrap text-muted tabular">{formatDateTime(s.receivedAt)}</td>
                    <td className="px-3">
                      <div className="font-semibold">{s.businessName ?? s.contactName ?? '—'}</div>
                      <div className="text-xs text-muted">
                        {[s.contactName, s.phoneE164 && formatPhone(s.phoneE164), s.email].filter(Boolean).join(' · ')}
                      </div>
                    </td>
                    <td className="px-3 text-xs">
                      <div>{s.formName ?? SOURCE_LABELS[s.source]}</div>
                      {s.pageUrl && <div className="ltr max-w-56 truncate text-muted">{safeDecodeUri(s.pageUrl)}</div>}
                    </td>
                    <td className="px-3 text-xs text-muted">
                      {[s.utmSource, s.utmCampaign].filter(Boolean).join(' / ') || (s.gclid ? 'Google Ads (gclid)' : s.fbclid ? 'Meta (fbclid)' : 'ישיר / אורגני')}
                    </td>
                    <td className="px-3">
                      <Badge variant={STATUS_VARIANT[s.status]}>{SUBMISSION_STATUS_LABELS[s.status]}</Badge>
                      {s.organizationId && (
                        <Link to={`/org/${s.organizationId}`} className="mt-1 block text-xs text-matcha-700 hover:underline">
                          {s.organizationName}
                        </Link>
                      )}
                      {s.error && <div className="mt-1 text-xs text-overdue">{s.error}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function ReviewCard({ review }: { review: Review }) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const invalidate = useInvalidateCrm();
  const resolve = useMutation({
    mutationFn: (v: { action: 'attach' | 'create_new' | 'reject'; organizationId?: string }) => api.post(`/inbound/reviews/${review.id}/resolve`, v),
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: ['reviews'] });
      void qc.invalidateQueries({ queryKey: ['submissions'] });
      invalidate(v.organizationId);
      toast.success(v.action === 'attach' ? 'הפנייה צורפה לרשומה הקיימת' : v.action === 'create_new' ? 'נוצר ליד חדש' : 'הפנייה סומנה כלא רלוונטית');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Card className="overflow-hidden border-today/30">
      <div className="grid gap-4 p-4 md:grid-cols-[1fr_1.2fr]">
        <div>
          <div className="text-xs font-bold text-muted">פנייה חדשה · {formatDateTime(review.receivedAt)} · {review.formName ?? SOURCE_LABELS[review.source]}</div>
          <div className="mt-1 text-lg font-semibold">{review.businessName ?? review.contactName}</div>
          <div className="text-sm text-ink-soft">
            {[review.contactName, review.phoneE164 && formatPhone(review.phoneE164), review.email, review.businessType, review.city].filter(Boolean).join(' · ')}
          </div>
          {review.message && <p className="mt-2 rounded-control bg-sunken p-2 text-sm whitespace-pre-wrap">{review.message}</p>}
        </div>
        <div className="flex flex-col gap-2">
          <div className="text-xs font-bold text-muted">רשומות קיימות דומות</div>
          {review.candidates.map((c) => (
            <div key={c.organizationId} className="flex flex-wrap items-center gap-2 rounded-control border border-line bg-surface p-2.5">
              <div className="min-w-0 flex-1">
                <Link to={`/org/${c.organizationId}`} className="font-semibold hover:underline" target="_blank">
                  {c.organizationName}
                </Link>
                <div className="text-xs text-muted">
                  {c.reasons.map((r) => MATCH_REASON_LABELS[r]).join(' · ')} · דמיון שם {Math.round(c.nameSimilarity * 100)}%
                </div>
              </div>
              {can('crm.write') && (
                <Button size="sm" variant="secondary" loading={resolve.isPending} onClick={() => resolve.mutate({ action: 'attach', organizationId: c.organizationId })}>
                  <GitMerge /> צירוף לרשומה זו
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>
      {can('crm.write') && (
        <div className="flex flex-wrap justify-end gap-2 border-t border-line bg-sunken/40 px-4 py-2.5">
          <Button size="sm" variant="ghost" onClick={() => resolve.mutate({ action: 'reject' })}>
            <X /> לא רלוונטי / ספאם
          </Button>
          <Button size="sm" onClick={() => resolve.mutate({ action: 'create_new' })}>
            <Plus /> זה עסק אחר — יצירת ליד חדש
          </Button>
        </div>
      )}
      {resolve.isSuccess && (
        <div className="flex items-center gap-2 px-4 py-2 text-sm text-success">
          <Check className="size-4" /> טופל
        </div>
      )}
    </Card>
  );
}
